<template>
  <!-- Mobile Bottom Navigation -->
  <div class="mobile-bottom-nav is-hidden-tablet">
    <!-- Main Navigation Items -->
    <div class="bottom-nav-items">
      <!-- Nexus/Admin Navigation -->
      <template v-if="isNexus">
        <router-link
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('admin-companies') }"
          :to="{ name: 'admin-companies' }"
        >
          <span class="bottom-nav-icon">
            <font-awesome-icon
              :icon="['fal', 'building-user']"
              :active-icon="['fadl', 'building-user']"
              :active="isActiveRoute('admin-companies')"
            />
          </span>
          <span class="bottom-nav-label">Companies</span>
        </router-link>

        <router-link
          v-if="canAccessDeliverables"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('admin-deliverables') }"
          :to="{ name: 'admin-deliverables' }"
        >
          <span class="bottom-nav-icon">
            <font-awesome-icon
              :icon="['fal', 'chart-kanban']"
              :active-icon="['fadl', 'chart-kanban']"
              :active="isActiveRoute('admin-deliverables')"
            />
          </span>
          <span class="bottom-nav-label">Deliverables</span>
        </router-link>

        <router-link
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('admin-calendar') }"
          :to="{ name: 'admin-calendar' }"
        >
          <span class="bottom-nav-icon">
            <font-awesome-icon
              :icon="['fal', 'calendar']"
              :active-icon="['fadl', 'calendar']"
              :active="isActiveRoute('admin-calendar')"
            />
          </span>
          <span class="bottom-nav-label">Calendar</span>
        </router-link>

        <router-link
          v-if="canAccessRoute('admin-conversations')"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('admin-conversations') }"
          :to="{ name: 'admin-conversations' }"
        >
          <span class="bottom-nav-icon">
            <font-awesome-icon
              :icon="['fal', 'message-lines']"
              :active-icon="['fadl', 'message-lines']"
              :active="isActiveRoute('admin-conversations')"
            />
          </span>
          <span class="bottom-nav-label">Messages</span>
        </router-link>

      </template>

      <!-- Client Navigation -->
      <template v-else-if="showClientNav">
        <!-- Vysual Only: flat nav items instead of dropdown -->
        <template v-if="clientIsVysualOnly">
          <router-link
            class="bottom-nav-item"
            :class="{ 'is-active': isActiveRoute('account-assets') }"
            :to="{
              name: 'account-assets',
              params: { id: currentRouteCompanyId },
            }"
          >
            <span class="bottom-nav-icon">
              <font-awesome-icon
                :icon="['fal', 'photo-film']"
                :active-icon="['fadl', 'photo-film']"
                :active="isActiveRoute('account-assets')"
              />
            </span>
            <span class="bottom-nav-label">Library</span>
          </router-link>

          <router-link
            v-if="clientCanAccessSharing"
            class="bottom-nav-item"
            :class="{ 'is-active': isActiveRoute('account-sharing') }"
            :to="{
              name: 'account-sharing',
              params: { id: currentRouteCompanyId },
            }"
          >
            <span class="bottom-nav-icon">
              <font-awesome-icon
                :icon="['fal', 'share-nodes']"
                :active-icon="['fadl', 'share-nodes']"
                :active="isActiveRoute('account-sharing')"
              />
            </span>
            <span class="bottom-nav-label">Sharing</span>
          </router-link>

          <router-link
            v-if="clientCanAccessUpload"
            class="bottom-nav-item"
            :class="{ 'is-active': isActiveRoute('account-upload') }"
            :to="{
              name: 'account-upload',
              params: { id: currentRouteCompanyId },
            }"
          >
            <span class="bottom-nav-icon">
              <font-awesome-icon
                :icon="['fal', 'cloud-upload']"
                :active-icon="['fadl', 'cloud-upload']"
                :active="isActiveRoute('account-upload')"
              />
            </span>
            <span class="bottom-nav-label">Upload</span>
          </router-link>
        </template>

        <!-- Standard: Assets dropdown when user has sub-pages, simple link otherwise -->
        <template v-else>
          <div
            v-if="isAdmin || clientCanAccessSharing"
            class="bottom-nav-item assets-menu"
            :class="{ 'is-active': isAssetsActive }"
          >
            <div
              class="dropdown is-up"
              :class="{ 'is-active': isAssetsMenuActive }"
            >
              <div class="dropdown-trigger" @click.stop="toggleAssetsMenu">
                <span class="bottom-nav-icon">
                  <font-awesome-icon
                    :icon="['fal', 'photo-film']"
                    :active-icon="['fadl', 'photo-film']"
                    :active="isAssetsActive"
                  />
                </span>
                <span class="bottom-nav-label">Assets</span>
              </div>
              <div class="dropdown-menu" role="menu">
                <div class="dropdown-content">
                  <router-link
                    class="dropdown-item"
                    :to="{
                      name: 'account-assets',
                      params: { id: currentRouteCompanyId },
                    }"
                    @click="closeAssetsMenu"
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fal', 'photo-film']" />
                    </span>
                    <span>Browse Assets</span>
                  </router-link>
                  <router-link
                    v-if="clientCanAccessUpload"
                    class="dropdown-item"
                    :to="{
                      name: 'account-upload',
                      params: { id: currentRouteCompanyId },
                    }"
                    @click="closeAssetsMenu"
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fal', 'upload']" />
                    </span>
                    <span>Upload</span>
                  </router-link>
                  <router-link
                    v-if="clientCanAccessSharing"
                    class="dropdown-item"
                    :to="{
                      name: 'account-sharing',
                      params: { id: currentRouteCompanyId },
                    }"
                    @click="closeAssetsMenu"
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fal', 'share-nodes']" />
                    </span>
                    <span>Sharing</span>
                  </router-link>
                  <router-link
                    v-if="isAdmin"
                    class="dropdown-item"
                    :to="{
                      name: 'account-assets',
                      params: { id: currentRouteCompanyId },
                      query: { manage: 'buckets' },
                    }"
                    @click="closeAssetsMenu"
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fal', 'folder-gear']" />
                    </span>
                    <span>Manage Buckets</span>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <router-link
            v-else
            class="bottom-nav-item"
            :class="{ 'is-active': isAssetsActive }"
            :to="{
              name: 'account-assets',
              params: { id: currentRouteCompanyId },
            }"
          >
            <span class="bottom-nav-icon">
              <font-awesome-icon
                :icon="['fal', 'photo-film']"
                :active-icon="['fadl', 'photo-film']"
                :active="isAssetsActive"
              />
            </span>
            <span class="bottom-nav-label">Assets</span>
          </router-link>
        </template>

        <router-link
          v-if="clientCanAccessProjects"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('account-projects') }"
          :to="{
            name: 'account-projects',
            params: { id: currentRouteCompanyId },
          }"
        >
          <span class="bottom-nav-icon">
            <font-awesome-icon
              :icon="['fal', 'rectangle-list']"
              :active-icon="['fadl', 'rectangle-list']"
              :active="isActiveRoute('account-projects')"
            />
          </span>
          <span class="bottom-nav-label">Projects</span>
        </router-link>

        <router-link
          v-if="clientCanAccessReviews"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('account-reviews') }"
          :to="{
            name: 'account-reviews',
            params: { id: currentRouteCompanyId },
          }"
        >
          <span class="bottom-nav-icon review-icon-wrapper">
            <font-awesome-icon
              :icon="['fal', 'list-check']"
              :active-icon="['fadl', 'list-check']"
              :active="isActiveRoute('account-reviews')"
            />
            <span
              v-if="hasActiveReviews"
              class="notification-dot mobile-notification-dot"
            ></span>
          </span>
          <span class="bottom-nav-label">Reviews</span>
        </router-link>

        <router-link
          v-if="clientCanAccessShows"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('account-shows') || isActiveRoute('account-show-detail') }"
          :to="{
            name: 'account-shows',
            params: { id: currentRouteCompanyId },
          }"
        >
          <span class="bottom-nav-icon podcast-icon-wrapper">
            <font-awesome-icon
              :icon="['fal', 'podcast']"
              :active-icon="['fadl', 'podcast']"
              :active="isActiveRoute('account-shows') || isActiveRoute('account-show-detail')"
            />
          </span>
          <span class="bottom-nav-label">Podcasts</span>
        </router-link>

        <router-link
          v-if="clientCanAccessCalendar"
          class="bottom-nav-item position-relative"
          :class="{ 'is-active': isActiveRoute('account-calendar') }"
          :to="{
            name: 'account-calendar',
            params: { id: currentRouteCompanyId },
          }"
        >
          <span class="bottom-nav-icon calendar-icon-wrapper">
            <font-awesome-icon
              :icon="['fal', 'calendar']"
              :active-icon="['fadl', 'calendar']"
              :active="isActiveRoute('account-calendar')"
            />
            <!-- Red dot notification for mobile -->
            <span
              v-if="hasProjectsNeedingDates"
              class="notification-dot mobile-notification-dot"
            ></span>
          </span>
          <span class="bottom-nav-label">Calendar</span>
        </router-link>

        <router-link
          v-if="clientCanAccessCompany"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('account-company-details') }"
          :to="{
            name: 'account-company-details',
            params: { id: currentRouteCompanyId },
          }"
        >
          <span class="bottom-nav-icon icon-with-dot">
            <font-awesome-icon
              :icon="['fal', 'building']"
              :active-icon="['fadl', 'building']"
              :active="isActiveRoute('account-company-details')"
            />
            <!-- Red dot notification for storage action required -->
            <span
              v-if="hasStorageActionRequired"
              class="notification-dot mobile-notification-dot"
            ></span>
          </span>
          <span class="bottom-nav-label">Company</span>
        </router-link>

        <router-link
          v-if="clientCanAccessConversations"
          class="bottom-nav-item"
          :class="{ 'is-active': isActiveRoute('account-conversations') }"
          :to="{
            name: 'account-conversations',
            params: { id: currentRouteCompanyId },
          }"
        >
          <span class="bottom-nav-icon review-icon-wrapper">
            <font-awesome-icon
              :icon="['fal', 'message-lines']"
              :active-icon="['fadl', 'message-lines']"
              :active="isActiveRoute('account-conversations')"
            />
            <span
              v-if="conversationsStore.unreadCount"
              class="notification-dot mobile-notification-dot"
            ></span>
          </span>
          <span class="bottom-nav-label">Messages</span>
        </router-link>
      </template>

      <!-- User actions moved to top navbar avatar dropdown -->
    </div>
  </div>

  <!-- Mobile Notifications Panel -->
  <div class="mobile-notifications-panel" :class="{ 'is-open': isMobileNotificationsOpen }">
    <div class="mobile-notifications-header">
      <span class="is-size-6 has-text-weight-semibold">Notifications</span>
      <button class="delete" aria-label="close" @click="closeMobileNotifications"></button>
    </div>
    <div class="mobile-notifications-body">
      <NotificationDropdown />
    </div>
  </div>
  <div
    v-if="isMobileNotificationsOpen"
    class="mobile-notifications-backdrop"
    @click="closeMobileNotifications"
  ></div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNavbarState } from '@/composables/useNavbarState'
import { useClientPermissions } from '@/composables/useClientPermissions'
import { usePermissions } from '@/composables/usePermissions'
import { useConversationsStore } from '@/stores/conversationsStore'
import NotificationDropdown from '@/components/notifications/NotificationDropdown.vue'

const conversationsStore = useConversationsStore()

const {
  isNexus,
  showClientNav,
  currentRouteCompanyId,
  isActiveRoute,
  isAssetsActive,
  isAdmin,
  hasProjectsNeedingDates,
  hasStorageActionRequired,
  hasActiveReviews,
  isMobileNotificationsOpen,
  closeMobileNotifications,
} = useNavbarState()

const { canAccessRoute } = usePermissions()
const canAccessDeliverables = computed(() => canAccessRoute('admin-deliverables'))

// Assets dropdown menu state
const isAssetsMenuActive = ref(false)
const toggleAssetsMenu = () => {
  isAssetsMenuActive.value = !isAssetsMenuActive.value
}
const closeAssetsMenu = () => {
  isAssetsMenuActive.value = false
}

// Close assets menu on outside click
const handleOutsideClick = (e) => {
  if (isAssetsMenuActive.value && !e.target.closest('.assets-menu')) {
    closeAssetsMenu()
  }
}
onMounted(() => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))

const {
  isVysualOnly: clientIsVysualOnly,
  canAccessUpload: clientCanAccessUpload,
  canAccessSharing: clientCanAccessSharing,
  canAccessProjects: clientCanAccessProjects,
  canAccessCalendar: clientCanAccessCalendar,
  canAccessCompany: clientCanAccessCompany,
  canAccessShows: clientCanAccessShows,
  canAccessReviews: clientCanAccessReviews,
  canAccessConversations: clientCanAccessConversations,
} = useClientPermissions()

</script>

<style lang="scss" scoped>
@import '@/assets/scss/navbar-shared';

// Mobile Bottom Navigation Styles
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bulma-white);
  border-top: 1px solid var(--bulma-border);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 30;
  padding: 0;
  padding-bottom: env(safe-area-inset-bottom, 0px);

  .bottom-nav-items {
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 0.5rem 0;
    max-width: 100%;
  }

  .bottom-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.25rem;
    min-height: 60px;
    flex: 1;
    text-decoration: none;
    color: var(--bulma-text-light);
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;

    &:hover {
      color: var(--bulma-primary);
      text-decoration: none;
      background-color: rgba(var(--bulma-primary-rgb), 0.05);
    }

    &.is-active {
      color: var(--bulma-primary);

      .bottom-nav-icon {
        color: var(--bulma-primary);
      }

      .bottom-nav-label {
        color: var(--bulma-primary);
        font-weight: 600;
      }
    }

    .bottom-nav-icon {
      font-size: 1.25rem;
      margin-bottom: 0.25rem;
      color: inherit;
      position: relative;

      .mobile-notification-dot {
        position: absolute;
        top: -4px;
        right: -4px;
        width: 8px;
        height: 8px;
        background-color: var(--bulma-danger);
        border-radius: 50%;
        border: 2px solid var(--bulma-white);
        z-index: 10;
        animation: pulse-dot 2s infinite;
      }
    }

    .bottom-nav-label {
      font-size: 0.7rem;
      font-weight: 500;
      text-align: center;
      line-height: 1;
      color: inherit;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }

    // Assets menu specific styles (same pattern as more-menu)
    &.assets-menu {
      position: relative;
      padding: 0;

      .dropdown {
        position: static;
        width: 100%;
        height: 100%;

        .dropdown-trigger {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          padding: 0.5rem 0.25rem;
          cursor: pointer;
          background: none;
          border: none;
          color: inherit;
          transition: all 0.2s ease;

          &:hover {
            color: var(--bulma-primary);
            background-color: rgba(var(--bulma-primary-rgb), 0.05);
          }

          .bottom-nav-icon {
            font-size: 1.25rem;
            margin-bottom: 0.25rem;
            color: inherit;
          }

          .bottom-nav-label {
            font-size: 0.7rem;
            font-weight: 500;
            text-align: center;
            line-height: 1;
            color: inherit;
            white-space: nowrap;
          }
        }

        &.is-active .dropdown-trigger {
          color: var(--bulma-primary);

          .bottom-nav-icon,
          .bottom-nav-label {
            color: var(--bulma-primary);
          }

          .bottom-nav-label {
            font-weight: 600;
          }
        }

        .dropdown-menu {
          position: absolute;
          bottom: 100%;
          left: -1rem;
          right: auto;
          top: auto;
          min-width: 280px;
          max-width: calc(100vw - 2rem);
          margin-bottom: 0.5rem;
          z-index: 50;
          box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);

          .dropdown-content {
            padding: 1rem;
            max-height: 50vh;
            overflow-y: auto;

            .dropdown-item {
              padding: 0.5rem;
              display: flex;
              align-items: center;
              gap: 0.5rem;
              cursor: pointer;

              &:not(:last-child) {
                margin-bottom: 0.5rem;
              }
            }
          }
        }

        &.is-active .dropdown-menu {
          display: block;
        }

        &.is-up .dropdown-menu {
          bottom: 100%;
          top: auto;
        }
      }
    }

    // More menu specific styles
    &.more-menu {
      position: relative;
      padding: 0;

      .dropdown {
        position: static;
        width: 100%;
        height: 100%;

        .dropdown-trigger {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          padding: 0.5rem 0.25rem;
          cursor: pointer;
          background: none;
          border: none;
          color: inherit;
          transition: all 0.2s ease;

          &:hover {
            color: var(--bulma-primary);
            background-color: rgba(var(--bulma-primary-rgb), 0.05);
          }

          .bottom-nav-icon {
            font-size: 1.25rem;
            margin-bottom: 0.25rem;
            color: inherit;
          }

          .bottom-nav-label {
            font-size: 0.7rem;
            font-weight: 500;
            text-align: center;
            line-height: 1;
            color: inherit;
            white-space: nowrap;
          }
        }

        &.is-active .dropdown-trigger {
          color: var(--bulma-primary);

          .bottom-nav-icon,
          .bottom-nav-label {
            color: var(--bulma-primary);
          }

          .bottom-nav-label {
            font-weight: 600;
          }
        }

        .dropdown-menu {
          position: absolute;
          bottom: 100%;
          right: -1rem;
          left: auto;
          top: auto;
          min-width: 280px;
          max-width: calc(100vw - 2rem);
          margin-bottom: 0.5rem;
          z-index: 50;
          box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);

          @media screen and (max-width: 350px) {
            right: -0.5rem;
            min-width: 250px;
          }

          .dropdown-content {
            padding: 1rem;
            max-height: 50vh;
            overflow-y: auto;

            .dropdown-item {
              padding: 0.5rem;
              display: flex;
              align-items: center;
              gap: 0.5rem;
              cursor: pointer;

              &:not(:last-child) {
                margin-bottom: 0.5rem;
              }
            }
          }
        }

        &.is-active .dropdown-menu {
          display: block;
        }

        &.is-up .dropdown-menu {
          bottom: 100%;
          top: auto;
        }
      }
    }
  }
}

// Mobile notifications panel
.mobile-notifications-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
}

.mobile-notifications-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--bulma-scheme-main);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  transform: translateY(100%);
  transition: transform 0.3s ease;

  &.is-open {
    transform: translateY(0);
  }

  .mobile-notifications-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1rem 0.5rem;
    flex-shrink: 0;
  }

  .mobile-notifications-body {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    // Remove the dropdown's own border-radius and shadow since we handle it
    :deep(.notification-dropdown) {
      width: 100%;
      max-width: 100%;
      box-shadow: none;
      border-radius: 0;
    }
  }
}
</style>
